import type { ContainerKind, IdControlCapabilities, IdPreview } from '@shared-ui/components';
import { adminApi } from '../api/adminApi';
import { CONTAINER_DISPLAY_FIELDS } from '../pages/settings/explorerSchema';
import { JsonNode } from '../pages/settings/ExplorerTab';

/**
 * Map container → in-app detail route. Return null when the admin console has
 * no dedicated detail page for that container (Explorer is then the fallback).
 */
function adminDetailRoute(container: ContainerKind, id: string): string | null {
  switch (container) {
    case 'submissions':         return `/submissions/${id}`;
    case 'users':               return `/users/${id}`;
    case 'hint-requests':       return `/hints/${id}`;
    case 'modelOutputs':        return `/model-data/${id}`;
    case 'evalDatasets':        return `/eval/datasets/${id}`;
    case 'paperimport-imports': return `/paper-imports/${id}`;
    // Pages that don't take an :id param — point at the list with a query hint.
    // NB: the Questions page reads `?id=` (matches the in-app /questions?id= links).
    case 'questions':           return `/questions?id=${id}`;
    case 'modules':             return `/modules?selected=${id}`;
    case 'prompts':             return `/prompts?selected=${id}`;
    case 'evalRuns':            return `/eval/runs?selected=${id}`;
    default:                    return null;
  }
}

function getNested(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>(
    (o, k) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[k] : undefined),
    obj,
  );
}

function toPreview(container: ContainerKind, doc: Record<string, unknown>): IdPreview {
  const fieldNames = CONTAINER_DISPLAY_FIELDS[container] || [];
  const fields = fieldNames
    .map(name => {
      const val = getNested(doc, name);
      if (val === undefined || val === null) return null;
      const s = typeof val === 'string' ? val : JSON.stringify(val);
      return { label: name, value: s.length > 80 ? s.slice(0, 80) + '…' : s };
    })
    .filter((f): f is { label: string; value: string } => f !== null);
  return { title: container, fields };
}

/**
 * Build the capability set for the admin console.
 * Receives `navigate` from a top-level component that has access to react-router.
 */
export function buildAdminCapabilities(navigate: (path: string) => void): IdControlCapabilities {
  return {
    detailRoute: adminDetailRoute,
    navigate,

    preview: async (container, id) => {
      const res = await adminApi.getExplorerDocument(container, id);
      return res.document ? toPreview(container, res.document) : null;
    },

    viewJson: async (container, id) => {
      const res = await adminApi.getExplorerDocument(container, id);
      return res.document;
    },

    renderJson: (doc, container) => {
      // Navigate inside the modal: send the user to Explorer for the linked doc.
      const handleFkNavigate = (targetContainer: string, targetId: string) => {
        navigate(`/monitoring?tab=explorer&container=${targetContainer}&id=${targetId}`);
      };
      return (
        <pre style={{ margin: 0, fontSize: 12, fontFamily: "'SF Mono', Monaco, monospace" }}>
          <JsonNode
            value={doc}
            container={container}
            fieldPath=""
            onNavigate={handleFkNavigate}
          />
        </pre>
      );
    },

    openExplorer: (container, id) => {
      navigate(`/monitoring?tab=explorer&container=${container}&id=${id}`);
    },

    // findReferences intentionally omitted — until ExplorerTab honors a `mode=references`
    // deep-link, this would behave identically to openExplorer. Re-add once the reverse-FK
    // panel can be opened from a URL param. The capability type stays in shared-ui.

    detailUrlBase: typeof window !== 'undefined' ? window.location.origin : undefined,
  };
}
